'use client'

import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ArrowLeft, PaintBucket } from 'phosphor-react'
import { Link } from '@/lib'
import Components from '../components'

export default function Painting() {

  const paintings: Array<{ title: string; image: string; description: string; year?: string }> = [
    {
      title: 'Cadillac',
      image: '/assets/creative-projects/adillac.jpg',
      description: 'Mixed-media artwork featuring a classic green Cadillac, painted over a collage of vintage automotive documents and forms',
      year: '2009'
    },
    {
      title: 'Arches National Park',
      image: '/assets/creative-projects/ArchesNP.jpg',
      description: 'Watercolor painting of Delicate Arch in Arches National Park, capturing the iconic red sandstone arch against a bright blue sky',
      year: '2020'
    },
    {
      title: 'Aviation Mural',
      image: '/assets/creative-projects/Aviation Mural.jpg',
      description: 'Mural celebrating the history of air travel, designed as an airmail envelope with depictions of blimps, space shuttles, biplanes, and early aviators',
      year: '2010'
    },
    {
      title: 'Bird Collage',
      image: '/assets/creative-projects/Birdcollage.JPG',
      description: 'Collage artwork featuring birds and natural elements',
      year: '2009'
    },
    {
      title: 'Cascades National Park',
      image: '/assets/creative-projects/CascadesNP.jpg',
      description: 'Watercolor landscape painting of a serene mountain lake surrounded by dense forests under a cloudy sky',
      year: '2020'
    },
    {
      title: 'Sand Dunes National Park',
      image: '/assets/creative-projects/SandDunesNP.JPG',
      description: 'Watercolor painting of rolling sand dunes in a desert landscape, capturing the warm, earthy tones of dawn or dusk',
      year: '2020'
    },
    {
      title: 'The Juki Singers',
      image: '/assets/creative-projects/The Juki Singers.jpg',
      description: 'Oil painting depicting a garment factory floor with workers operating sewing machines, exploring themes of industrial labor and anonymity',
      year: '2010'
    },
    {
      title: 'Viking Mural',
      image: '/assets/creative-projects/Viking Mural.jpg',
      description: 'Collaborative mural with James Penfield and Brenda Taylor. Mural depicting Viking exploration and the Kensington Runestone, featuring Viking longships, maps, and historical narrative',
      year: '2009'
    },
    {
      title: 'Voyageurs National Park',
      image: '/assets/creative-projects/VoyagersNP.jpg',
      description: 'Watercolor painting of a serene landscape featuring a small, rocky island covered with trees in a calm body of water',
      year: '2020'
    },
    {
      title: 'Death Valley National Park',
      image: '/assets/creative-projects/DeathValleyNP.JPG',
      description: 'Vibrant painting depicting a dramatic starry night sky over a rugged, mountainous desert landscape with a bright yellow-orange horizon glow',
      year: '2020'
    }
  ]

  return (
    <Components.PageContainer>
      <Components.SkipLink />
      <Components.GlassNavigation />
      
      <main id="main-content" tabIndex={-1} className="pt-16 md:pt-32 pb-16 px-4 md:px-6" role="main" aria-label="Painting">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="mb-8 md:mb-12">
            <Button
              asChild
              variant="ghost"
              className="mb-6 text-[var(--brutal-ink)] hover:bg-[var(--brutal-panel)]"
            >
              <Link to="/CreativeProjects">
                <ArrowLeft weight="duotone" className="w-4 h-4 mr-2" />
                Back to Creative Projects
              </Link>
            </Button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 aspect-square bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <PaintBucket weight="duotone" className="w-6 h-6 text-[var(--brutal-ink)]" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--brutal-ink)] leading-tight break-words">
                  Painting
                </h1>
                <p className="text-base md:text-lg text-[var(--brutal-muted)] mt-2 break-words">
                  Traditional and digital painting explorations exploring color, composition, and expressive mark-making
                </p>
              </div>
            </div>
          </header>

          {/* Gallery Grid */}
          {paintings.length > 0 ? (
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" aria-label="Painting gallery">
              {paintings.map((painting, index) => (
                <Card
                  key={index}
                  className="group relative overflow-hidden brutal-panel hover:bg-[var(--brutal-kraft)] transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl !p-0 !gap-0"
                >
                  <Components.ImageModal
                    src={painting.image}
                    alt={painting.title}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden border-b-2 border-[var(--brutal-ink)] cursor-pointer">
                      <img
                        src={painting.image}
                        alt={painting.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </Components.ImageModal>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-[var(--brutal-ink)] mb-1 group-hover:text-[var(--brutal-ink)] transition-colors duration-200 break-words">
                      {painting.title}
                    </h3>
                    {painting.year && (
                      <p className="text-[var(--brutal-muted)] text-xs mb-2">{painting.year}</p>
                    )}
                    <p className="text-[var(--brutal-muted)] text-sm leading-relaxed break-words">
                      {painting.description}
                    </p>
                  </div>
                </Card>
              ))}
            </section>
          ) : (
            <Card className="brutal-panel p-12 text-center">
              <p className="text-[var(--brutal-muted)] text-lg">Paintings coming soon...</p>
            </Card>
          )}
        </div>
      </main>

    </Components.PageContainer>
  )
}
